import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

// 🔍 V311 DEBUG: Safe-area diagnostics for Android
const logSafeAreaDiagnostics = () => {
  if (typeof window === 'undefined') return;
  
  const html = document.documentElement;
  const body = document.body;
  
  // Check platform class
  const hasPlatformAndroidHtml = html.classList.contains('platform-android');
  const hasPlatformAndroidBody = body.classList.contains('platform-android');
  
  // Get CSS variables
  const computedStyle = getComputedStyle(html);
  const satVar = computedStyle.getPropertyValue('--sat').trim();
  const sabVar = computedStyle.getPropertyValue('--sab').trim();
  
  // Test env() via temporary element
  const testDiv = document.createElement('div');
  testDiv.style.paddingTop = 'env(safe-area-inset-top, 0px)';
  testDiv.style.position = 'absolute';
  testDiv.style.visibility = 'hidden';
  document.body.appendChild(testDiv);
  const envSatValue = getComputedStyle(testDiv).paddingTop;
  document.body.removeChild(testDiv);
  
  console.log('🔍 V311 SAFE-AREA DIAGNOSTICS:', {
    platformAndroidHtml: hasPlatformAndroidHtml,
    platformAndroidBody: hasPlatformAndroidBody,
    cssVarSat: satVar || '(empty)',
    cssVarSab: sabVar || '(empty)',
    envSafeAreaInsetTop: envSatValue,
    userAgent: navigator.userAgent.includes('Android') ? 'Android detected' : 'Not Android'
  });
};

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

// 🎯 V184.1.2: Dialog overlay uses z-[80] to sit well above bottom navigation (z-40)
// 🎯 V329.3.1: Bumped to z-[10100] to beat chat header (z-[10050]) and composer — fixes Block/Report behind header
const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-[10100] bg-black/60 backdrop-blur-sm dialog-overlay-android-fix",
      "data-[state=open]:animate-in data-[state=closed]:animate-out",
      "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

interface DialogContentProps extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  hideCloseButton?: boolean;
}

function subtreeHasDialogDescription(node: React.ReactNode): boolean {
  let found = false
  React.Children.forEach(node, (child) => {
    if (found || !React.isValidElement(child)) return
    const type = child.type as { displayName?: string } | string | undefined
    const displayName =
      type != null && typeof type !== "string" && "displayName" in type
        ? type.displayName
        : undefined
    if (type === DialogPrimitive.Description || displayName === "DialogDescription") {
      found = true
      return
    }
    const nested = (child.props as { children?: React.ReactNode }).children
    if (nested != null && subtreeHasDialogDescription(nested)) found = true
  })
  return found
}

/** Radix warns when Content has a description id but no Description. Passing undefined opts out. */
function descriptionSilenceProps(
  children: React.ReactNode,
  ariaDescribedBy: React.AriaAttributes["aria-describedby"]
): { "aria-describedby": undefined } | Record<string, never> {
  if (ariaDescribedBy !== undefined || subtreeHasDialogDescription(children)) return {}
  return { "aria-describedby": undefined }
}

// 🎯 V311: CONTAINER-LEVEL SAFE-AREA CONTRACT
// Establishes reliable safe-area handling that works even when env() returns 0
const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, hideCloseButton = false, ...props }, ref) => {
  // 🔍 V311: Run diagnostics on mount
  React.useEffect(() => {
    logSafeAreaDiagnostics();
  }, []);

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        data-kind="standard"
        className={cn(
          // Base styles — V329.3.1: z-[10101] to sit above overlay (z-[10100])
          "fixed z-[10101] flex flex-col bg-background shadow-lg border border-border",
          // V329.3.2: Viewport-bounded sizing — w-[calc(100vw-2rem)] mobile, max-h with dvh
          "bottom-0 left-[1rem] right-[1rem] w-[calc(100vw-2rem)] rounded-t-3xl",
          "max-h-[calc(100dvh-2rem)] overflow-hidden",
          // Desktop: centered card
          "sm:bottom-auto sm:left-[50%] sm:right-auto sm:top-[54%] sm:w-full sm:max-w-lg",
          "sm:translate-x-[-50%] sm:translate-y-[-50%] sm:rounded-3xl",
          // Animations
          "duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out",
          "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
          "data-[state=closed]:slide-out-to-bottom-[48%] data-[state=open]:slide-in-from-bottom-[48%]",
          "sm:data-[state=closed]:slide-out-to-left-1/2 sm:data-[state=open]:slide-in-from-left-1/2",
          "sm:data-[state=closed]:slide-out-to-top-[48%] sm:data-[state=open]:slide-in-from-top-[48%]",
          className
        )}
        style={{
          '--dlg-safe-top': 'max(var(--sat, 0px), 24px)',
          '--dlg-safe-bot': 'max(var(--sab, 0px), 0px)',
          bottom: 'max(1rem, env(safe-area-inset-bottom, 0px))',
          maxHeight: 'min(calc(100dvh - 2rem), calc(90vh - var(--dlg-safe-top) - var(--dlg-safe-bot)))',
          ...(props.style as React.CSSProperties)
        } as React.CSSProperties}
        {...props}
        {...descriptionSilenceProps(children, props["aria-describedby"])}
      >
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-6 pb-[max(1rem,env(safe-area-inset-bottom))]">
          {children}
        </div>
        {!hideCloseButton && (
          <DialogPrimitive.Close
            className={cn(
              "absolute right-4",
              "rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100",
              "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
              "disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
            )}
            style={{
              // V311: Safe close button positioning
              top: 'calc(1rem + var(--dlg-safe-top, 24px))'
            }}
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
})
DialogContent.displayName = DialogPrimitive.Content.displayName

// Fullscreen dialog content for paywall dialogs - no centering transforms, full viewport
const FullscreenDialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, ...props }, ref) => (
    <DialogPortal>
      {/* V213.4: Overlay starts below status bar on Android, no backdrop-blur on Android */}
    <DialogOverlay className="fixed inset-0 z-[10100] bg-black/70 backdrop-blur-sm platform-android:backdrop-blur-none platform-android:bg-black/50 dialog-overlay-android-fix" />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        // V205.9: REAL FULLSCREEN FLEX CONTAINER — V329.3.1: z-[10101] above overlay
        "fixed inset-0 z-[10101] flex h-screen w-screen items-stretch justify-center p-0 outline-none",
        className
      )}
      {...props}
      {...descriptionSilenceProps(props.children, props["aria-describedby"])}
    />
  </DialogPortal>
))
FullscreenDialogContent.displayName = "FullscreenDialogContent"

// 🎯 V311: Fullscreen Safe Dialog Content - for Android safe-area compliant fullscreen dialogs
// Designed for Disconnect/Block/Report dialogs that need full-bleed backgrounds but safe interactive areas
interface FullscreenSafeDialogContentProps extends DialogContentProps {
  // Optional: override default safe top padding (default: 24px on Android)
  safeTopFallback?: string;
}

const FullscreenSafeDialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  FullscreenSafeDialogContentProps
>(({ className, children, hideCloseButton = false, safeTopFallback = '24px', ...props }, ref) => {
  // 🔍 V314: Run diagnostics on mount + temporary debug assertion
  React.useEffect(() => {
    logSafeAreaDiagnostics();
    console.log('✅ V314: FullscreenSafeDialogContent mounted with data-kind="fullscreen-safe"');
  }, []);

  // V313.2: Android devices need larger fallback (44px) to clear status bar
  // iOS and other platforms can use the smaller default (24px)
  const resolvedSafeTopFallback =
    typeof document !== "undefined" && document.documentElement.classList.contains("platform-android")
      ? "44px"
      : safeTopFallback;

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        data-kind="fullscreen-safe"
        className={cn(
          // V311: Fullscreen container — V329.3.1: z-[10101] above overlay, beats chat header (z-[10050])
          // V329.3.2: min-h-0 allows flex children to shrink for overflow; max-h ensures viewport bound
          "fixed inset-0 z-[10101] flex min-h-0 flex-col bg-background",
          "overflow-hidden",
          className
        )}
        style={{
          '--dlg-safe-top': `max(var(--sat, 0px), ${resolvedSafeTopFallback})`,
          '--dlg-safe-bot': 'max(var(--sab, 0px), 0px)',
          paddingTop: `var(--dlg-safe-top)`,
          paddingBottom: `var(--dlg-safe-bot)`,
          maxHeight: '100dvh',
          ...(props.style as React.CSSProperties)
        } as React.CSSProperties}
        {...props}
        {...descriptionSilenceProps(children, props["aria-describedby"])}
      >
        {children}
        {!hideCloseButton && (
          <DialogPrimitive.Close
            className={cn(
              "absolute end-4 z-50",
              "rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100",
              "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
              "disabled:pointer-events-none"
            )}
            data-testid="fullscreen-safe-dialog-close"
            style={{
              // V313.1: Use --dlg-safe-top for reliable Android safe-area positioning
              top: 'calc(1rem + var(--dlg-safe-top, 24px))'
            }}
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
})
FullscreenSafeDialogContent.displayName = "FullscreenSafeDialogContent"

// Centered dialog content - V311: Safe-area contract applied
// keyboardSafe is CenteredDialogContent-only so DialogContent / FullscreenSafeDialogContent
// cannot forward it to the DOM.
interface CenteredDialogContentProps extends DialogContentProps {
  keyboardSafe?: boolean;
}

const CenteredDialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  CenteredDialogContentProps
>(({ className, children, hideCloseButton = false, keyboardSafe = false, ...props }, ref) => {
  // 🔍 V311: Run diagnostics on mount
  React.useEffect(() => {
    logSafeAreaDiagnostics();
  }, []);

  return (
    <DialogPortal>
      <DialogOverlay />
      {/* Wrapper div with flexbox for TRUE viewport centering — V329.3.1: z-[10101] above overlay */}
      <div
        className={
          keyboardSafe
            ? "fixed inset-x-0 z-[10101] flex min-h-0 items-center justify-center p-4 pointer-events-none"
            : "fixed inset-0 z-[10101] flex items-end sm:items-center justify-center pointer-events-none"
        }
        style={
          keyboardSafe
            ? {
                top: 'var(--vvt, 0px)',
                height: 'var(--vvh, var(--app-height, 100dvh))',
              }
            : undefined
        }
      >
        <DialogPrimitive.Content
          ref={ref}
          className={cn(
            // Base styles - pointer-events-auto to make content clickable
            "pointer-events-auto flex flex-col bg-background shadow-lg border border-border",
            // V329.3.2: Viewport-bounded sizing
            keyboardSafe
              ? "mx-[1rem] w-[calc(100vw-2rem)] rounded-3xl max-h-full overflow-hidden sm:mx-0 sm:w-[50vw] sm:min-w-[400px] sm:max-w-[600px]"
              : "mx-[1rem] w-[calc(100vw-2rem)] rounded-t-3xl max-h-[calc(100dvh-2rem)] overflow-hidden sm:mx-0 sm:rounded-3xl sm:w-[50vw] sm:min-w-[400px] sm:max-w-[600px]",
            // Animations
            "duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            "data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
            "sm:data-[state=closed]:slide-out-to-bottom-0 sm:data-[state=open]:slide-in-from-bottom-0",
            className
          )}
          style={{
            '--dlg-safe-top': 'max(var(--sat, 0px), 24px)',
            '--dlg-safe-bot': 'max(var(--sab, 0px), 0px)',
            maxHeight: keyboardSafe
              ? '100%'
              : 'min(calc(100dvh - 2rem), calc(90vh - var(--dlg-safe-top) - var(--dlg-safe-bot)))',
            ...(props.style as React.CSSProperties)
          } as React.CSSProperties}
          {...props}
          {...descriptionSilenceProps(children, props["aria-describedby"])}
        >
          <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-6 pb-[max(1rem,env(safe-area-inset-bottom))]">
            {children}
          </div>
          {!hideCloseButton && (
            <DialogPrimitive.Close
              className={cn(
                "absolute right-4",
                "rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100",
                "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
                "disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
              )}
              style={{
                // V311: Safe close button positioning
                top: 'calc(1rem + var(--dlg-safe-top, 24px))'
              }}
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Content>
      </div>
    </DialogPortal>
  );
})
CenteredDialogContent.displayName = "CenteredDialogContent"

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className
    )}
    {...props}
  />
)
DialogHeader.displayName = "DialogHeader"

// 🎯 V201.4: Responsive dialog title using V201.3 typography utilities
const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "heading-sm leading-none tracking-tight",
      className
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  FullscreenDialogContent,
  FullscreenSafeDialogContent,
  CenteredDialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
}

